import { useTranslation } from 'react-i18next';
import { formatModel, formatOs } from '@scio3/shared-types';
import { formatDateMedium } from '@/lib/format-date';
import { safeDistanceToNow } from '@/lib/safe-format';
import type { MdmDevice } from '../hooks/useStaffCustomerDevices';

// Phase 7 T070 — shared formatters take DeviceClassification. Fall back to the
// raw model + '—' if classification hasn't propagated to an older cached row.
function resolveFormattedModel(d: MdmDevice): string {
  return d.classification ? formatModel(d.classification) : (d.model ?? '—');
}
function resolveFormattedOs(d: MdmDevice): string {
  return d.classification ? formatOs(d.classification) : '—';
}

/**
 * Flat labeled key-value rows for the device detail drawer.
 * Only renders rows that have a real value — hides rather than showing blanks.
 *
 * Fields intentionally shown on the customer side (VPN IP is excluded —
 * staff-only per spec §9.3).
 */
export function DeviceInfoList({ device }: { device: MdmDevice }) {
  const { t } = useTranslation();

  // featureDevice is a polymorphic JSON blob from scio1; any field we reach
  // into here must be accessed narrowly. Prefer top-level Device fields first.
  const rows: Array<{ label: string; value: React.ReactNode; key: string }> = [];

  const add = (key: string, label: string, value: React.ReactNode | undefined | null) => {
    if (value === undefined || value === null || value === '') return;
    rows.push({ key, label, value });
  };

  add('uuid', t('UUID'), device.UUID);
  add('serial', t('Serial'), device.serialNo);
  // Friendly model with raw code shown as subdued subtitle for debug.
  const formattedModel = resolveFormattedModel(device);
  const rawModel = device.model && device.model !== formattedModel ? device.model : null;
  add(
    'model',
    t('Model'),
    <span>
      {formattedModel}
      {rawModel ? <span className="ml-2 text-xs text-muted-foreground">{rawModel}</span> : null}
    </span>,
  );
  add('os', t('OS'), resolveFormattedOs(device));
  add('localAppVersion', t('MDM Ver'), device.localAppVersion);
  add('platform', t('Platform'), device.platform);
  add('manufacturer', t('Manufacturer'), device.manufacturer);
  add('tz', t('Timezone'), device.tz);
  add('activationDate', t('Paired'), formatDateMedium(device.activationDate, ''));
  add(
    'lastHeartBeat',
    t('Online since'),
    device.lastHeartBeat ? safeDistanceToNow(device.lastHeartBeat, { addSuffix: true }, '') : '',
  );
  if (device.fleetId !== undefined && device.fleetId !== null) {
    add('fleetId', t('Fleet'), device.fleetId);
  }
  // Staff delta: surface VPN IP on the detail drawer (and region if present).
  // apps/web's copy intentionally omits these rows (spec §9.3 — staff-only).
  const staffDevice = device as typeof device & { vpnIp?: string; vpnRegion?: string };
  if (staffDevice.vpnIp) {
    add('vpnIp', t('VPN IP'), staffDevice.vpnIp);
  }
  if (staffDevice.vpnRegion) {
    add('vpnRegion', t('VPN Region'), staffDevice.vpnRegion);
  }

  if (rows.length === 0) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="mdm-device-info-empty">
        {t('No details available')}
      </div>
    );
  }

  return (
    <dl
      className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm"
      data-testid="mdm-device-info-list"
    >
      {rows.map((row) => (
        <div key={row.key} className="contents">
          <dt className="text-muted-foreground">{row.label}</dt>
          <dd className="font-medium break-all">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}
